import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ramsAPI } from '../../../api';
import Layout from '../../../components/Layout';
import { Button } from '../../../components/ui/Button';
import { Badge } from '../../../components/ui/Badge';
import { Card, CardContent } from '../../../components/ui/Card';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '../../../components/ui/Table';
import { ArrowLeft, Edit, FileDown, GitBranch } from 'lucide-react';
import { format } from 'date-fns';

const STATUS_LABELS = { draft: 'Draft', issued: 'Issued', approved: 'Approved', archived: 'Archived' };
const LINK_ENTITY_LABELS = { job: 'Job', invoice: 'Invoice', estimate: 'Estimate' };

function siteSnapshotDisplay(siteSnapshot) {
  if (!siteSnapshot || typeof siteSnapshot !== 'object') return null;
  const addr = siteSnapshot.address;
  const line1 = typeof addr === 'string' ? addr : addr?.line1 ?? addr?.line_1 ?? '';
  const parts = [
    siteSnapshot.site_name,
    siteSnapshot.client_name,
    line1,
    addr?.line2 ?? addr?.line_2,
    addr?.city,
    addr?.county,
    addr?.postcode,
  ].filter(Boolean);
  return parts.length ? parts : null;
}

function controlMeasureText(cm) {
  if (typeof cm === 'string') return cm;
  if (cm && typeof cm === 'object' && cm.text) return cm.text;
  return null;
}

export default function RamsView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [rams, setRams] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pdfLoading, setPdfLoading] = useState(false);
  const [statusTransitioning, setStatusTransitioning] = useState(false);

  const STATUS_TRANSITIONS = {
    draft: [
      { value: 'issued', label: 'Issue' },
      { value: 'archived', label: 'Archive' },
    ],
    issued: [
      { value: 'approved', label: 'Approve' },
      { value: 'archived', label: 'Archive' },
      { value: 'draft', label: 'Back to Draft' },
    ],
    approved: [{ value: 'archived', label: 'Archive' }],
    archived: [],
  };

  useEffect(() => {
    if (!id) {
      setLoading(false);
      setError('Invalid RAMS ID');
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError('');
        const data = await ramsAPI.getOne(id);
        if (!cancelled) setRams(data);
      } catch (err) {
        if (!cancelled) setError(err.message || 'Failed to load RAMS');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  const loadRams = async () => {
    if (!id) return;
    try {
      setLoading(true);
      setError('');
      const data = await ramsAPI.getOne(id);
      setRams(data);
    } catch (err) {
      setError(err.message || 'Failed to load RAMS');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPdf = async () => {
    if (!id || !rams) return;
    try {
      setPdfLoading(true);
      const blob = await ramsAPI.getPDF(id);
      const url = URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `${(rams.title || 'RAMS').replace(/[^\w\s-]/g, '')}-v${rams.version ?? 1}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.message || 'Failed to download PDF');
    } finally {
      setPdfLoading(false);
    }
  };

  const handleStatusTransition = async (newStatus) => {
    if (!id) return;
    try {
      setStatusTransitioning(true);
      setError('');
      await ramsAPI.updateStatus(id, newStatus);
      const data = await ramsAPI.getOne(id);
      setRams(data);
    } catch (err) {
      setError(err.message || 'Failed to update status');
    } finally {
      setStatusTransitioning(false);
    }
  };

  const handleNewVersion = async () => {
    if (!id) return;
    try {
      setStatusTransitioning(true);
      setError('');
      const created = await ramsAPI.newVersion(id);
      if (created?.id) navigate(`/tenant/tools/rams/${created.id}/edit`, { replace: false });
    } catch (err) {
      setError(err.message || 'Failed to create new version');
    } finally {
      setStatusTransitioning(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="max-w-5xl mx-auto px-6 py-8">
          <div className="flex items-center justify-center py-16">
            <div className="inline-block h-8 w-8 animate-spin rounded-full border-2 border-gray-300 dark:border-gray-600 border-t-blue-600" />
            <span className="ml-3 text-sm text-gray-600 dark:text-gray-400">Loading RAMS...</span>
          </div>
        </div>
      </Layout>
    );
  }

  if (error || !rams) {
    return (
      <Layout>
        <div className="max-w-5xl mx-auto px-6 py-8">
          <div className="rounded-md bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 p-4">
            <p className="text-sm text-red-700 dark:text-red-300">{error || 'RAMS not found'}</p>
            <Button
              variant="outline"
              size="sm"
              className="mt-3"
              onClick={() => navigate('/tenant/tools/rams')}
            >
              <ArrowLeft className="h-4 w-4 mr-1.5" />
              Back to RAMS
            </Button>
          </div>
        </div>
      </Layout>
    );
  }

  const siteParts = siteSnapshotDisplay(rams.siteSnapshot);
  const hazards = Array.isArray(rams.hazards) ? rams.hazards : [];
  const methodSteps = Array.isArray(rams.methodSteps) ? rams.methodSteps : [];
  const ppe = Array.isArray(rams.ppe) ? rams.ppe : [];
  const tools = Array.isArray(rams.tools) ? rams.tools : [];
  const permits = Array.isArray(rams.permits) ? rams.permits : [];
  const signatures = Array.isArray(rams.signatures) ? rams.signatures : [];

  return (
    <Layout>
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-5xl mx-auto px-6 py-4">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-3 min-w-0">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/tenant/tools/rams')}
                className="shrink-0"
              >
                <ArrowLeft className="h-4 w-4" />
              </Button>
              <div className="min-w-0">
                <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100 truncate">
                  {rams.title || 'RAMS'}
                </h1>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-0.5">
                  Version {rams.version ?? 1} · {STATUS_LABELS[rams.status] ?? rams.status}
                </p>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2 shrink-0">
              {(STATUS_TRANSITIONS[rams.status] ?? []).map((t) => (
                <Button
                  key={t.value}
                  variant={t.value === 'approved' ? 'primary' : 'outline'}
                  size="sm"
                  onClick={() => handleStatusTransition(t.value)}
                  disabled={statusTransitioning}
                >
                  {t.label}
                </Button>
              ))}
              <Button
                variant="outline"
                size="sm"
                onClick={handleNewVersion}
                disabled={statusTransitioning}
              >
                <GitBranch className="h-4 w-4 mr-1.5" />
                New version
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate(`/tenant/tools/rams/${rams.id}/edit`)}
              >
                <Edit className="h-4 w-4 mr-1.5" />
                Edit
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={handleDownloadPdf}
                disabled={pdfLoading}
              >
                <FileDown className="h-4 w-4 mr-1.5" />
                {pdfLoading ? 'Downloading...' : 'Download PDF'}
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-8 space-y-8">
        {/* Header / Site snapshot */}
        <Card className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
          <CardContent className="p-6">
            <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100 border-b border-gray-200 dark:border-gray-700 pb-2 mb-4">
              Document details
            </h2>
            {rams.description && (
              <p className="text-gray-700 dark:text-gray-300 text-sm mb-4">{rams.description}</p>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-500 dark:text-gray-400">Version</span>
                <span className="ml-2 text-gray-900 dark:text-gray-100">{rams.version ?? 1}</span>
              </div>
              <div>
                <span className="text-gray-500 dark:text-gray-400">Status</span>
                <span className="ml-2">
                  <Badge variant={rams.status === 'approved' ? 'success' : 'neutral'}>
                    {STATUS_LABELS[rams.status] ?? rams.status}
                  </Badge>
                </span>
              </div>
              {rams.status === 'approved' && (rams.approvedAt || rams.approvedBy) && (
                <div className="md:col-span-2">
                  <span className="text-gray-500 dark:text-gray-400">Approved</span>
                  <span className="ml-2 text-gray-900 dark:text-gray-100">
                    {rams.approvedAt && format(new Date(rams.approvedAt), 'dd/MM/yyyy HH:mm')}
                    {rams.approvedByEmail && (
                      <span className="text-gray-600 dark:text-gray-400 ml-1">
                        by {rams.approvedByEmail}
                      </span>
                    )}
                  </span>
                </div>
              )}
              {rams.linkEntityType && (
                <div>
                  <span className="text-gray-500 dark:text-gray-400">Linked to</span>
                  <span className="ml-2 text-gray-900 dark:text-gray-100">
                    {LINK_ENTITY_LABELS[rams.linkEntityType] ?? rams.linkEntityType}
                  </span>
                </div>
              )}
            </div>
            {siteParts && (
              <div className="mt-4 p-4 rounded-md bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600">
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Site snapshot</p>
                <p className="text-sm text-gray-900 dark:text-gray-100 whitespace-pre-line">
                  {siteParts.join('\n')}
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Compliance and disclaimers */}
        <Card className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
          <CardContent className="p-6">
            <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100 border-b border-gray-200 dark:border-gray-700 pb-2 mb-4">
              UK compliance and disclaimers
            </h2>
            <div className="text-sm text-gray-700 dark:text-gray-300 space-y-3">
              <p>
                The duty holder and competent person remain responsible for the suitability and application of this document. This RAMS does not replace a site-specific assessment where required.
              </p>
              <p className="text-gray-600 dark:text-gray-400 italic">
                Any AI-generated content is draft or suggestions only and does not replace the duty holder or competent person responsibility.
              </p>
              {rams.complianceText && (
                <div className="pt-2 border-t border-gray-200 dark:border-gray-700">
                  <p className="whitespace-pre-wrap">{rams.complianceText}</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Risk assessment table */}
        <Card className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
          <CardContent className="p-6">
            <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100 border-b border-gray-200 dark:border-gray-700 pb-2 mb-4">
              Risk assessment
            </h2>
            {hazards.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400">No hazards recorded.</p>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow isHeader>
                      <TableHead>Category</TableHead>
                      <TableHead>Hazard</TableHead>
                      <TableHead>Persons at risk</TableHead>
                      <TableHead>Initial (L/S/R)</TableHead>
                      <TableHead>Residual (L/S/R)</TableHead>
                      <TableHead>Control measures</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {hazards.map((h) => {
                      const controls = Array.isArray(h.controlMeasures) ? h.controlMeasures : [];
                      const controlTexts = controls.map(controlMeasureText).filter(Boolean);
                      const par = Array.isArray(h.personsAtRisk) ? h.personsAtRisk : [];
                      return (
                        <TableRow key={h.id}>
                          <TableCell className="text-gray-900 dark:text-gray-100 text-sm">
                            {h.hazardCategory || '—'}
                          </TableCell>
                          <TableCell className="text-gray-700 dark:text-gray-300 text-sm">
                            {h.hazardText || '—'}
                          </TableCell>
                          <TableCell className="text-gray-600 dark:text-gray-400 text-sm">
                            {par.length ? par.join(', ') : '—'}
                          </TableCell>
                          <TableCell className="text-gray-600 dark:text-gray-400 text-sm tabular-nums">
                            {[h.initialLikelihood, h.initialSeverity, h.initialRisk].every((n) => n != null)
                              ? `${h.initialLikelihood}/${h.initialSeverity}/${h.initialRisk}`
                              : '—'}
                          </TableCell>
                          <TableCell className="text-gray-600 dark:text-gray-400 text-sm tabular-nums">
                            {[h.residualLikelihood, h.residualSeverity, h.residualRisk].every((n) => n != null)
                              ? `${h.residualLikelihood}/${h.residualSeverity}/${h.residualRisk}`
                              : '—'}
                          </TableCell>
                          <TableCell className="text-gray-700 dark:text-gray-300 text-sm">
                            {controlTexts.length ? (
                              <ul className="list-disc list-inside space-y-0.5">
                                {controlTexts.map((t, i) => (
                                  <li key={i}>{t}</li>
                                ))}
                              </ul>
                            ) : '—'}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Method statement */}
        <Card className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
          <CardContent className="p-6">
            <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100 border-b border-gray-200 dark:border-gray-700 pb-2 mb-4">
              Method statement
            </h2>
            {methodSteps.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400">No steps recorded.</p>
            ) : (
              <ol className="list-decimal list-inside space-y-4">
                {methodSteps
                  .sort((a, b) => (a.stepOrder ?? 0) - (b.stepOrder ?? 0))
                  .map((step) => (
                    <li key={step.id} className="text-sm">
                      <span className="font-medium text-gray-900 dark:text-gray-100">
                        {step.stepTitle || 'Step'}
                      </span>
                      {Array.isArray(step.stepDetail) && step.stepDetail.length > 0 && (
                        <ul className="ml-6 mt-1 list-disc text-gray-700 dark:text-gray-300 space-y-0.5">
                          {step.stepDetail.map((d, i) => (
                            <li key={i}>{typeof d === 'string' ? d : String(d)}</li>
                          ))}
                        </ul>
                      )}
                    </li>
                  ))}
              </ol>
            )}
          </CardContent>
        </Card>

        {/* PPE */}
        <Card className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
          <CardContent className="p-6">
            <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100 border-b border-gray-200 dark:border-gray-700 pb-2 mb-4">
              PPE
            </h2>
            {ppe.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400">None listed.</p>
            ) : (
              <ul className="list-disc list-inside text-sm text-gray-700 dark:text-gray-300 space-y-0.5">
                {ppe
                  .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
                  .map((p) => (
                    <li key={p.id}>
                      {p.item}
                      {p.required === false && (
                        <span className="text-gray-500 dark:text-gray-400 ml-1">(optional)</span>
                      )}
                    </li>
                  ))}
              </ul>
            )}
          </CardContent>
        </Card>

        {/* Tools / equipment */}
        <Card className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
          <CardContent className="p-6">
            <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100 border-b border-gray-200 dark:border-gray-700 pb-2 mb-4">
              Tools and equipment
            </h2>
            {tools.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400">None listed.</p>
            ) : (
              <ul className="list-disc list-inside text-sm text-gray-700 dark:text-gray-300 space-y-0.5">
                {tools
                  .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
                  .map((t) => (
                    <li key={t.id}>{t.item}</li>
                  ))}
              </ul>
            )}
          </CardContent>
        </Card>

        {/* Permits */}
        <Card className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
          <CardContent className="p-6">
            <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100 border-b border-gray-200 dark:border-gray-700 pb-2 mb-4">
              Permits and authorisations
            </h2>
            {permits.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400">None listed.</p>
            ) : (
              <ul className="space-y-2 text-sm">
                {permits
                  .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
                  .map((p) => (
                    <li key={p.id} className="text-gray-700 dark:text-gray-300">
                      <span className="font-medium">{p.permitType}</span>
                      {p.required === false && (
                        <span className="text-gray-500 dark:text-gray-400 ml-1">(optional)</span>
                      )}
                      {p.notes && (
                        <span className="block text-gray-600 dark:text-gray-400 mt-0.5">{p.notes}</span>
                      )}
                    </li>
                  ))}
              </ul>
            )}
          </CardContent>
        </Card>

        {/* Emergency arrangements */}
        <Card className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
          <CardContent className="p-6">
            <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100 border-b border-gray-200 dark:border-gray-700 pb-2 mb-4">
              Emergency arrangements
            </h2>
            {rams.emergencyArrangements ? (
              <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                {rams.emergencyArrangements}
              </p>
            ) : (
              <p className="text-sm text-gray-500 dark:text-gray-400">None specified.</p>
            )}
          </CardContent>
        </Card>

        {/* Sign-off */}
        <Card className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
          <CardContent className="p-6">
            <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100 border-b border-gray-200 dark:border-gray-700 pb-2 mb-4">
              Sign-off
            </h2>
            {signatures.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400">No signatures recorded.</p>
            ) : (
              <div className="space-y-4">
                {signatures.map((sig) => (
                  <div
                    key={sig.id}
                    className="flex flex-wrap items-baseline gap-2 text-sm border-b border-gray-100 dark:border-gray-700 pb-3 last:border-0 last:pb-0"
                  >
                    <span className="font-medium text-gray-900 dark:text-gray-100">{sig.signerName}</span>
                    {sig.signerRole && (
                      <span className="text-gray-500 dark:text-gray-400">{sig.signerRole}</span>
                    )}
                    <span className="text-gray-500 dark:text-gray-400">
                      {sig.signedAt ? format(new Date(sig.signedAt), 'dd/MM/yyyy HH:mm') : ''}
                    </span>
                    {sig.signatureText && (
                      <span className="block w-full text-gray-600 dark:text-gray-400 italic mt-1">
                        {sig.signatureText}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <div className="flex items-center gap-2 pt-4">
          <Button variant="outline" size="sm" onClick={() => navigate('/tenant/tools/rams')}>
            <ArrowLeft className="h-4 w-4 mr-1.5" />
            Back to RAMS
          </Button>
        </div>
      </div>
    </Layout>
  );
}
